// OIDC trust-policy tab body — extracted from the original
// AdminOIDCPolicies page so it can render inside the consolidated
// `/admin/trust` shell alongside the X.509 and Sigstore tabs.
//
// Layout — three sections top-to-bottom:
//   * "New / Edit policy" form — single form that doubles for create
//     and edit modes (toggled via local `editingId` state).
//   * "Policies" table — every policy with inline enable/disable +
//     delete. Click "Edit" to load a row into the form above.
//   * "Test a token" panel — paste a JWT, see which policy would
//     match. Calls /api/admin/oidc/policies/test which runs the
//     same matcher as the live exchange but skips signature verify
//     and skips token mint. Useful for iterating subject_pattern.

import { useState } from 'react';
import { Plus, Trash2, Edit, Beaker, KeyRound } from 'lucide-react';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import {
  useOIDCPolicies,
  createOIDCPolicy,
  updateOIDCPolicy,
  deleteOIDCPolicy,
  testOIDCToken,
  type OIDCPolicy,
  type OIDCPolicyInput,
  type OIDCTestMatchResponse,
} from '../../api/oidc';

function emptyDraft(): OIDCPolicyInput {
  return {
    project_id: '',
    name: '',
    issuer: 'https://gitlab.com',
    audience: 'orbitalreg',
    subject_pattern: 'project_path:[^:]+:ref_type:branch:ref:main',
    claim_filters: {},
    grants_role: 'publisher',
    grants_repos: [],
    default_ttl_seconds: 1800,
    max_ttl_seconds: 14400,
    enabled: true,
  };
}

export function AdminTrustOidcTab() {
  const { data, mutate } = useOIDCPolicies();
  const policies: OIDCPolicy[] = data?.policies ?? [];

  const [draft, setDraft] = useState<OIDCPolicyInput>(emptyDraft);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [filtersText, setFiltersText] = useState('{}');
  const [reposText, setReposText] = useState('');

  const [testJWT, setTestJWT] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<OIDCTestMatchResponse | null>(null);
  const [testErr, setTestErr] = useState<string | null>(null);

  const startEdit = (p: OIDCPolicy) => {
    setEditingId(p.id);
    setDraft({
      project_id: p.project_id,
      name: p.name,
      issuer: p.issuer,
      audience: p.audience,
      subject_pattern: p.subject_pattern,
      claim_filters: p.claim_filters ?? {},
      grants_role: p.grants_role,
      grants_repos: p.grants_repos ?? [],
      service_account_id: p.service_account_id ?? null,
      default_ttl_seconds: p.default_ttl_seconds,
      max_ttl_seconds: p.max_ttl_seconds,
      enabled: p.enabled,
    });
    setFiltersText(JSON.stringify(p.claim_filters ?? {}, null, 2));
    setReposText((p.grants_repos ?? []).join(', '));
    setErr(null);
  };

  const resetForm = () => {
    setEditingId(null);
    setDraft(emptyDraft());
    setFiltersText('{}');
    setReposText('');
    setErr(null);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      let parsedFilters: Record<string, unknown> = {};
      if (filtersText.trim() !== '') {
        try {
          parsedFilters = JSON.parse(filtersText);
        } catch (parseErr) {
          throw new Error(`claim_filters must be valid JSON: ${(parseErr as Error).message}`);
        }
      }
      const repos = reposText
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);

      const payload: OIDCPolicyInput = {
        ...draft,
        claim_filters: parsedFilters,
        grants_repos: repos,
      };

      if (editingId) {
        await updateOIDCPolicy(editingId, payload);
      } else {
        await createOIDCPolicy(payload);
      }
      await mutate();
      resetForm();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id: string) => {
    if (!confirm('Delete this OIDC trust policy? In-flight tokens already minted from it will not be revoked.')) {
      return;
    }
    await deleteOIDCPolicy(id);
    if (editingId === id) {
      resetForm();
    }
    await mutate();
  };

  const onToggleEnabled = async (p: OIDCPolicy) => {
    await updateOIDCPolicy(p.id, {
      project_id: p.project_id,
      name: p.name,
      issuer: p.issuer,
      audience: p.audience,
      subject_pattern: p.subject_pattern,
      claim_filters: p.claim_filters ?? {},
      grants_role: p.grants_role,
      grants_repos: p.grants_repos ?? [],
      service_account_id: p.service_account_id ?? null,
      default_ttl_seconds: p.default_ttl_seconds,
      max_ttl_seconds: p.max_ttl_seconds,
      enabled: !p.enabled,
    });
    await mutate();
  };

  const onTest = async (e: React.FormEvent) => {
    e.preventDefault();
    setTesting(true);
    setTestErr(null);
    setTestResult(null);
    try {
      const res = await testOIDCToken(testJWT.trim());
      setTestResult(res);
    } catch (e) {
      setTestErr((e as Error).message);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-8">
      <Card>
        <CardHeader
          title={
            <span className="flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-nebula" />
              {editingId ? 'Edit policy' : 'New policy'}
            </span>
          }
          description="One row per (issuer, audience, subject regex) match rule. Multiple policies in the same project are tried first-match-wins."
        />
        <CardBody>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <label className="space-y-1 text-sm">
                <span className="text-fg-muted">Project ID (UUID)</span>
                <Input
                  required
                  value={draft.project_id}
                  onChange={(e) => setDraft({ ...draft, project_id: e.target.value })}
                  placeholder="00000000-0000-0000-0000-000000000000"
                />
              </label>
              <label className="space-y-1 text-sm">
                <span className="text-fg-muted">Policy name</span>
                <Input
                  required
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="gitlab-main-branch"
                />
              </label>
              <label className="space-y-1 text-sm">
                <span className="text-fg-muted">Issuer (https://)</span>
                <Input
                  required
                  value={draft.issuer}
                  onChange={(e) => setDraft({ ...draft, issuer: e.target.value })}
                />
              </label>
              <label className="space-y-1 text-sm">
                <span className="text-fg-muted">Audience</span>
                <Input
                  required
                  value={draft.audience}
                  onChange={(e) => setDraft({ ...draft, audience: e.target.value })}
                />
              </label>
            </div>

            <label className="block space-y-1 text-sm">
              <span className="text-fg-muted">Subject pattern (Go RE2 regex)</span>
              <Input
                required
                value={draft.subject_pattern}
                onChange={(e) => setDraft({ ...draft, subject_pattern: e.target.value })}
                placeholder="project_path:acme/billing:ref_type:branch:ref:main"
              />
            </label>

            <label className="block space-y-1 text-sm">
              <span className="text-fg-muted">Claim filters (JSON object — optional equality checks)</span>
              <textarea
                value={filtersText}
                onChange={(e) => setFiltersText(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 rounded-md bg-bg-inset text-fg border border-border font-mono text-xs"
                placeholder='{"ref_protected": "true"}'
              />
            </label>

            <div className="grid md:grid-cols-2 gap-4">
              <label className="space-y-1 text-sm">
                <span className="text-fg-muted">Grants role</span>
                <select
                  value={draft.grants_role}
                  onChange={(e) =>
                    setDraft({ ...draft, grants_role: e.target.value as OIDCPolicyInput['grants_role'] })
                  }
                  className="h-10 w-full px-3 rounded-md bg-bg-inset text-fg border border-border"
                >
                  <option value="reader">reader</option>
                  <option value="publisher">publisher</option>
                  <option value="maintainer">maintainer</option>
                </select>
              </label>
              <label className="space-y-1 text-sm">
                <span className="text-fg-muted">Grants repos (comma-separated slugs; empty = all)</span>
                <Input
                  value={reposText}
                  onChange={(e) => setReposText(e.target.value)}
                  placeholder="libs, services/api"
                />
              </label>
              <label className="space-y-1 text-sm">
                <span className="text-fg-muted">Default TTL (seconds, 60–14400)</span>
                <Input
                  type="number"
                  min={60}
                  max={14400}
                  value={draft.default_ttl_seconds ?? 1800}
                  onChange={(e) =>
                    setDraft({ ...draft, default_ttl_seconds: Number.parseInt(e.target.value, 10) || 1800 })
                  }
                />
              </label>
              <label className="space-y-1 text-sm">
                <span className="text-fg-muted">Max TTL (seconds, 60–14400)</span>
                <Input
                  type="number"
                  min={60}
                  max={14400}
                  value={draft.max_ttl_seconds ?? 14400}
                  onChange={(e) =>
                    setDraft({ ...draft, max_ttl_seconds: Number.parseInt(e.target.value, 10) || 14400 })
                  }
                />
              </label>
              <label className="space-y-1 text-sm md:col-span-2">
                <span className="text-fg-muted">
                  Service account ID (optional — empty = pick first enabled SA in project)
                </span>
                <Input
                  value={draft.service_account_id ?? ''}
                  onChange={(e) =>
                    setDraft({ ...draft, service_account_id: e.target.value.trim() || null })
                  }
                  placeholder="leave blank to auto-resolve"
                />
              </label>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.enabled ?? true}
                onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
              />
              <span>Enabled (disabled policies match no tokens)</span>
            </label>

            {err && (
              <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
                {err}
              </div>
            )}

            <div className="flex items-center gap-3">
              <Button type="submit" variant="primary" disabled={busy}>
                <Plus className="h-4 w-4" />
                <span>{editingId ? 'Save changes' : 'Create policy'}</span>
              </Button>
              {editingId && (
                <Button type="button" variant="secondary" onClick={resetForm}>
                  Cancel edit
                </Button>
              )}
            </div>
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Policies"
          description="Click a row to edit. Toggle Enabled to disable matching without losing the configuration."
        />
        <CardBody>
          {policies.length === 0 ? (
            <p className="text-sm text-fg-muted">No policies yet — create one above.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-fg-muted border-b border-border">
                    <th className="py-2 pr-4">Name</th>
                    <th className="py-2 pr-4">Project</th>
                    <th className="py-2 pr-4">Issuer / aud</th>
                    <th className="py-2 pr-4">Role</th>
                    <th className="py-2 pr-4">TTL</th>
                    <th className="py-2 pr-4">Enabled</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {policies.map((p) => (
                    <tr key={p.id} className="border-b border-border/50">
                      <td className="py-2 pr-4 font-mono text-xs">{p.name}</td>
                      <td className="py-2 pr-4 font-mono text-xs">{p.project_id.slice(0, 8)}…</td>
                      <td className="py-2 pr-4 text-xs">
                        <div>{p.issuer}</div>
                        <div className="text-fg-muted">aud={p.audience}</div>
                      </td>
                      <td className="py-2 pr-4">
                        <Badge tone={p.grants_role === 'maintainer' ? 'warning' : 'accent'}>
                          {p.grants_role}
                        </Badge>
                      </td>
                      <td className="py-2 pr-4 text-xs">{p.default_ttl_seconds}s</td>
                      <td className="py-2 pr-4">
                        <button
                          onClick={() => onToggleEnabled(p)}
                          className="text-xs underline"
                        >
                          {p.enabled ? 'on' : 'off'}
                        </button>
                      </td>
                      <td className="py-2 flex items-center gap-2 justify-end">
                        <Button size="sm" variant="ghost" onClick={() => startEdit(p)}>
                          <Edit className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => onDelete(p.id)}>
                          <Trash2 className="h-3.5 w-3.5 text-danger" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title={
            <span className="flex items-center gap-2">
              <Beaker className="h-4 w-4 text-accent" />
              Test a token
            </span>
          }
          description="Paste an OIDC ID-token (signature is NOT verified — this only checks subject_pattern + claim_filters). Useful when iterating on a regex."
        />
        <CardBody>
          <form onSubmit={onTest} className="space-y-3">
            <textarea
              value={testJWT}
              onChange={(e) => setTestJWT(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 rounded-md bg-bg-inset text-fg border border-border font-mono text-xs"
              placeholder="eyJhbGciOiJSUzI1NiIs..."
            />
            <Button type="submit" variant="secondary" disabled={testing || testJWT.trim() === ''}>
              {testing ? 'Testing…' : 'Run match'}
            </Button>
            {testErr && (
              <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
                {testErr}
              </div>
            )}
            {testResult && (
              <div
                className={
                  'rounded-md px-3 py-2 text-sm border ' +
                  (testResult.matched
                    ? 'border-accent/40 bg-accent/10'
                    : 'border-warning/40 bg-warning/10')
                }
              >
                {testResult.matched ? (
                  <div className="space-y-1">
                    <div>
                      <strong>Matched policy:</strong> {testResult.policy_name} ({testResult.policy_id})
                    </div>
                    <div>
                      <strong>Project:</strong> {testResult.project_id}
                    </div>
                    <div>
                      <strong>Subject:</strong> <code>{testResult.sub}</code>
                    </div>
                    <div>
                      <strong>Scopes:</strong>{' '}
                      <code className="text-xs">{(testResult.scopes ?? []).join(' · ')}</code>
                    </div>
                    <div>
                      <strong>Token TTL:</strong> {testResult.expires_in_seconds}s
                    </div>
                    {testResult.resolved_service_account && (
                      <div>
                        <strong>Service account:</strong> {testResult.resolved_service_account}
                      </div>
                    )}
                    {testResult.service_account_resolution_error && (
                      <div className="text-danger">
                        SA resolution would fail: {testResult.service_account_resolution_error}
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <strong>No match.</strong> {testResult.reason}
                    {testResult.sub && (
                      <>
                        <br />
                        <span className="text-fg-muted">
                          sub: <code>{testResult.sub}</code>
                        </span>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
